import { useState } from "react";
import { useLang } from "@/i18n";
import DisclaimerModal from "@/components/DisclaimerModal";
import { Eyebrow, FramedPhoto, KineticLines, ParallaxImage, Reveal } from "@/components/motion";

const Biography = () => {
  const { t } = useLang();
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const b = t.bio;

  return (
    <main data-testid="biography-page" className="pt-36">
      {/* INTRO */}
      <section className="mx-auto grid max-w-[1440px] items-start gap-16 px-6 md:grid-cols-12 lg:px-12">
        <div className="md:col-span-7">
          <Eyebrow>{b.eyebrow}</Eyebrow>
          <h1 className="mt-6 font-heritage text-5xl italic tracking-tight sm:text-7xl">
            <KineticLines lines={[b.title]} delay={0.15} />
          </h1>
          <Reveal delay={0.4}>
            <p className="drop-cap mt-12 max-w-2xl text-lg leading-relaxed text-neutral-300" data-testid="bio-intro">
              {b.intro}
            </p>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-neutral-300" data-testid="bio-intro-2">
              {b.intro2}
            </p>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-neutral-300" data-testid="bio-intro-3">
              {b.intro3}
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-5">
          <Reveal delay={0.25} testId="bio-portrait-hero">
            <FramedPhoto src="/images/jano-portrait-main.jpg" alt="Vittorio Jano" caption={b.capMain} />
          </Reveal>
        </div>
      </section>

      {/* TIMELINE */}
      <section data-testid="bio-timeline" className="mx-auto max-w-[1440px] px-6 py-32 lg:px-12">
        <div className="relative ml-2 border-l border-white/15 md:ml-8">
          {b.timeline.map((e, i) => (
            <Reveal key={e.year} delay={0.05} testId={`bio-era-${e.year}`} className="relative pb-20 pl-10 last:pb-0 md:pl-16">
              <span className="absolute -left-[7px] top-2 h-3.5 w-3.5 rotate-45 bg-[#4A7BA6]" aria-hidden="true" />
              <div className="grid gap-6 md:grid-cols-12">
                <div className="md:col-span-3">
                  <span className="font-tech text-5xl font-bold tracking-tight text-white/90">{e.year}</span>
                </div>
                <div className="md:col-span-9">
                  <h3 className="font-heritage text-3xl italic text-white">{e.title}</h3>
                  <p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-400">{e.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* QUOTE */}
      <section className="border-y border-white/10 py-32">
        <div className="mx-auto grid max-w-[1440px] items-center gap-16 px-6 md:grid-cols-12 lg:px-12">
          <div className="md:col-span-5">
            <Reveal testId="bio-portrait-secondary">
              <FramedPhoto src="/images/jano-seated.jpg" alt="Vittorio Jano at the wheel" caption={b.capSeated} imgClassName="aspect-[4/5] object-top" />
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.15}>
              <blockquote className="font-heritage text-3xl italic leading-snug text-white/90 sm:text-5xl" data-testid="bio-quote">
                {b.quote}
              </blockquote>
              <div className="mt-10 h-px w-24 bg-[#4A7BA6]" />
              <button
                data-testid="bio-disclaimer-button"
                onClick={() => setDisclaimerOpen(true)}
                className="mt-10 inline-flex items-center font-tech text-sm uppercase tracking-[0.25em] text-white transition-colors hover:text-[#4A7BA6]"
              >
                {b.disclaimer.button}
              </button>
            </Reveal>
          </div>
        </div>
      </section>
      <DisclaimerModal open={disclaimerOpen} onClose={() => setDisclaimerOpen(false)} />
    </main>
  );
};

export default Biography;
